<template>
  <v-container
    class="products-list-page"
    fluid>
    <v-form ref="form">
      <v-row>
        <v-col
          cols="12"
          class="white pa-4 rounded">
          <div class="d-flex mb-4">
            <p class="text-h5 font-weight-bold">
              Product Weight and Dimension
            </p>
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              dark
              :loading="exporting || getting"
              :disabled="!products.length"
              @click="showDialogConfirm()">
              EXPORT
              <v-icon class="ml-1">
                mdi-file
              </v-icon>
            </v-btn>
          </div>
          <search-by-tags-and-brand
            v-model="query"
            append-icon="mdi-magnify"
            tags-width="300px"
            show-collection
            :is-mobile="false"
            @on-search="searchProduct()" />
        </v-col>
        <v-col
          v-if="getting"
          cols="12"
          class="pa-0 mt-4">
          <div class="d-flex justify-center align-center text-center list-height white">
            <v-icon
              large
              class="mdi-spin"
              color="secondary">
              mdi-loading
            </v-icon>
          </div>
        </v-col>
        <v-col
          v-else-if="!getting && !products.length"
          cols="12"
          class="pa-0 mt-4"
        >
          <div class="d-flex justify-center align-center text-center list-height white">
            ไม่พบข้อมูล
          </div>
        </v-col>
        <v-col
          v-else
          cols="12"
          class="mt-4 pa-4 list-height"
        >
          <v-row
            v-for="(product, index) in products"
            :key="product.id"
            :class="{ 'mb-6': index !== products.length - 1 }"
            class="white rounded">
            <v-col cols="12">
              <div class="d-flex  mt-2 mb-3">
                <img
                  v-if="product.photoUrls.length"
                  :src="$convertImgix(`${product.photoUrls[0]}?w=80`)"
                  class="elevation-1 main-image mr-4"
                  alt="product-image"
                  style="width:80px"
                >
                <img
                  v-else
                  :src="noImgAvailable"
                  class="elevation-1 main-image mr-4"
                  alt="product-image"
                  style="width:80px"
                >
                <v-row dense>
                  <v-col cols="6">
                    <div class="info-label">
                      PRODUCT NAME
                    </div>
                    <div class="info-value">
                      {{ product.name }}
                    </div>
                  </v-col>

                  <v-col cols="6">
                    <div class="info-label">
                      BRAND
                    </div>
                    <div class="info-value">
                      {{ product.brand.toUpperCase() }}
                    </div>
                  </v-col>

                  <v-col cols="6">
                    <div class="info-label">
                      COLLECTION
                    </div>
                    <div class="info-value">
                      <p
                        v-if="product.gwCollection"
                        class="mb-0">
                        {{ product.gwCollection.name.toUpperCase() || '-' }}
                      </p>
                      <p
                        v-else
                        class="mb-0">
                        -
                      </p>
                    </div>
                  </v-col>

                  <v-col cols="6">
                    <div class="info-label">
                      COLOR
                    </div>
                    <div class="info-value">
                      {{ product.colors[0].toUpperCase() || '-' }}
                    </div>
                  </v-col>
                </v-row>
              </div>
              <v-data-table
                :headers="headers"
                :items="product.skus"
                hide-default-footer
                class="elevation-1"
              >
                <template v-slot:item.code="{ item: sku }">
                  <div :class="{ 'text-top mt-2': editInfo && editInfo.id === sku.id }">
                    <p class="mb-0">
                      {{ sku.code }}
                    </p>
                  </div>
                </template>

                <template v-slot:item.size="{ item: sku }">
                  <div :class="{ 'text-top mt-2': editInfo && editInfo.id === sku.id }">
                    <p class="mb-0">
                      {{ sku.size.name.toUpperCase() }}
                    </p>
                  </div>
                </template>

                <template v-slot:item.weightGram="{ item: sku }">
                  <p
                    v-if="!editInfo || editInfo.id !== sku.id"
                    class="mb-0 text-center"
                  >
                    <span v-if="sku.weightGram">
                      {{ sku.weightGram | showNumberFormat }}
                    </span>
                    <span v-else>-</span>
                  </p>
                  <div
                    v-else
                    class="d-flex align-start mt-2 justify-center">
                    <v-text-field
                      v-model.number="editInfo.weightGram"
                      color="secondary"
                      :rules="weightRules"
                      placeholder="น้ำหนัก"
                      label="น้ำหนัก"
                      outlined
                      dense
                      class="max-width-text-field"
                    />
                  </div>
                </template>

                <template v-slot:item.dimension="{ item: sku }">
                  <div v-if="!editInfo || editInfo.id !== sku.id">
                    <p
                      v-if="sku.dimension && (sku.dimension.widthCm || sku.dimension.lengthCm || sku.dimension.heightCm)"
                      class="mb-0 text-center"
                    >
                      {{ $options.filters.showNumberFormat(sku.dimension.widthCm, 0, 1) }} x
                      {{ $options.filters.showNumberFormat(sku.dimension.lengthCm, 0, 1) }} x
                      {{ $options.filters.showNumberFormat(sku.dimension.heightCm, 0, 1) }}
                    </p>
                    <p
                      v-else
                      class="mb-0 text-center">
                      -
                    </p>
                  </div>
                  <div
                    v-else
                    class="d-flex align-start mt-2 justify-center"
                  >
                    <v-text-field
                      v-model.number="editInfo.dimension.widthCm"
                      color="secondary"
                      :rules="dimensionRules"
                      label="กว้าง"
                      placeholder="กว้าง"
                      outlined
                      dense
                      class="mr-3 max-width-text-field"
                    />
                    <v-text-field
                      v-model.number="editInfo.dimension.lengthCm"
                      color="secondary"
                      :rules="dimensionRules"
                      label="ยาว"
                      placeholder="ยาว"
                      outlined
                      dense
                      class="mr-3 max-width-text-field"
                    />
                    <v-text-field
                      v-model.number="editInfo.dimension.heightCm"
                      color="secondary"
                      :rules="dimensionRules"
                      label="สูง"
                      placeholder="สูง"
                      outlined
                      dense
                      class="max-width-text-field"
                    />
                  </div>
                </template>

                <template v-slot:item.actions="{ item: sku }">
                  <v-btn
                    v-if="!editInfo || editInfo.id !== sku.id"
                    icon
                    @click="setEdit(sku)"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <div
                    v-else
                    class="text-top mt-2">
                    <v-btn
                      color="secondary"
                      small
                      :loading="saving"
                      @click="updateWeightAndDimension()"
                    >
                      SAVE
                    </v-btn>

                    <v-btn
                      :loading="saving"
                      class="ml-2"
                      icon
                      color="grey"
                      circle
                      small
                      @click="clearEdit()"
                    >
                      <v-icon>mdi-close-circle</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-col>
        <v-col class="text-center">
          <v-pagination
            v-model="query.page"
            color="secondary"
            :total-visible="7"
            :length="totalPage"
            @input="changePage">
          </v-pagination>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>

import ProductProvider from '@/resources/ProductProvider'
import { mapActions } from 'vuex'
import ConvertQueryStringToArray from '@/assets/js/ConvertQueryStringToArray'
import SearchByTagsAndBrand from '@/components/SearchByTagsAndBrand.vue'
import NoImgAvailable from '@/assets/image/no_picture_available.png'
import { ExportToCsv } from 'export-to-csv'
import { escapeCsv } from '@/assets/js/StringFormat.js'
import SizeValues from '@/assets/js/SizeValues'

const ProductService = new ProductProvider()
export default {
  components: {
    SearchByTagsAndBrand
  },
  data () {
    return {
      products: [],
      query: {
        page: 1,
        limit: 15,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        search: '',
        tags: [],
        tagOperation: 'OR',
        brand: 'all',
        collection: ''
      },
      totalPage: 1,
      totalProduct: 0,
      getting: false,
      saving: false,
      headers: [
        { text: 'SKU CODE', value: 'code', align: 'center', sortable: false, width: 200 },
        { text: 'SIZE', value: 'size', align: 'center', sortable: false },
        { text: 'น้ำหนัก (กรัม)', value: 'weightGram', align: 'center', sortable: false },
        { text: 'ขนาด (กว้างxยาวxสูง)', value: 'dimension', align: 'center', sortable: false },
        { text: 'แก้ไข', value: 'actions', align: 'center', sortable: false }
      ],
      noImgAvailable: NoImgAvailable,
      editInfo: null,
      weightRules: [
        (v) => {
          const hasValue = v !== null && v !== undefined && v !== ''
          const value = String(v ?? '').trim()

          if (this.isAnyDimensionFilled && !hasValue) return true
          if (!hasValue) return 'กรุณาระบุ'
          if (!/^\d+$/.test(value) || Number(value) <= 0) {
            return 'ต้องเป็นจำนวนเต็มบวกและไม่มีทศนิยม'
          }

          return true
        }
      ],
      dimensionRules: [
        (v) => {
          const hasValue = v !== null && v !== undefined && v !== ''
          const value = String(v ?? '').trim()

          if (!this.isAnyDimensionFilled && this.editInfo?.weightGram) return true
          if (!hasValue) return 'กรุณาระบุ'
          if (
            (Number(value) < 0)
            || value.includes('-')
            || (value.includes('.') && !/^\d+\.\d{1}$/.test(value))
            || !/^\d+(\.\d+)?$/.test(value)
          ) {
           return 'ค่าต้องมากกว่าหรือเท่ากับ 0 ทศนิยมไม่เกิน 1 ตำแหน่ง'
          }

          return true
        }
      ],
      exporting: false
    }
  },
  computed: {
     isAnyDimensionFilled () {
      const d = this.editInfo?.dimension || {}

      return this.isFilled(d.widthCm)
            || this.isFilled(d.lengthCm)
            || this.isFilled(d.heightCm)
    }
  },
  watch: {
    editInfo: {
      handler (newVal, oldVal) {
        if (!oldVal) return
        this.revalidate()
      },
      deep: true
    }
  },
  async mounted () {
    await this.fetchProducts()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal'
    }),
    queryTags () {
      const result = ConvertQueryStringToArray(this.$route.query)
      this.query.search = result.search ? result.search : ''
      this.query.tags = result.tags ? result.tags : []
      this.query.tagOperation = result.tagOperation ? result.tagOperation : 'OR'
      this.query.brand = result.brand ? result.brand : 'all'
      this.query.collection = result.collection ? result.collection : ''
      this.query.page = result.page ? Number(result.page) : 1
    },
    searchProduct () {
      this.query.page = 1
      this.getting = false

      this.products = []
      this.$router.push({
        name: 'ProductWeightAndDimension',
        query: {
          page: this.query.page,
          search: this.query.search,
          tags: this.query.tags || [],
          tagOperation: this.query.tagOperation,
          brand: this.query.brand,
          collection: this.query.collection
        }
      }).catch((error) => {
        console.error(error)
      })
      this.fetchProducts()
    },
    changePage () {
      this.getting = false

      this.products = []
      this.$router.push({
        name: 'ProductWeightAndDimension',
        query: {
          page: this.query.page,
          search: this.query.search,
          tags: this.query.tags || [],
          tagOperation: this.query.tagOperation,
          brand: this.query.brand,
          collection: this.query.collection
        }
      }).catch((error) => {
        console.error(error)
      })
      this.fetchProducts()
    },
    async fetchProducts () {
      this.getting = true
        try {
          this.queryTags()

          const { data } = await ProductService.getProductsInfo({
            page: this.query.page,
            limit: this.query.limit,
            search: this.query.search,
            tags: this.query.tags,
            tagOperation: this.query.tagOperation,
            brand: this.query.brand,
            collection: this.query.collection
          })
          this.totalPage = data.pages
          this.totalProduct = data.total
          this.products = data.results.map((item) => {
            const { skus, ...product } = item
            return {
              ...product,
              skus: this.sortSize(skus)
            }
          })
        } catch (error) {
          console.error('fetchProducts', error)
          this.setErrorPage(error.code)
        } finally {
          this.getting = false
        }
    },
    setEdit (item) {
      this.editInfo = JSON.parse(JSON.stringify(item))
    },
    clearEdit () {
      this.editInfo = null
    },
    revalidate () {
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate()
        }
      })
    },
    async updateWeightAndDimension () {
      if (!this.$refs.form.validate()) return
      this.saving = true
      if (!this.editInfo) return
      try {
        await ProductService.updateWeightAndDimension({
          skuId: this.editInfo?.id,
          dimension: {
            widthCm: this.isFilled(this.editInfo.dimension.widthCm) ? Number(this.editInfo.dimension.widthCm) : null,
            lengthCm: this.isFilled(this.editInfo.dimension.lengthCm) ? Number(this.editInfo.dimension.lengthCm) : null,
            heightCm: this.isFilled(this.editInfo.dimension.heightCm) ? Number(this.editInfo.dimension.heightCm) : null
          },
          weightGram: this.isFilled(this.editInfo.weightGram) ? Number(this.editInfo.weightGram) : null
        })
        await this.fetchProducts()
        this.clearEdit()
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'การบันทึกสำเร็จ',
          type: 'success'
        })
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error.message ?? 'เกิดข้อผิดพลาด',
          type: 'error'
        })
      } finally {
        this.saving = false
      }
    },
    showDialogConfirm () {
      const limit = 3000
      const totalRound = Math.ceil(this.totalProduct / limit)

      this.setModal({
        value: true,
        title: 'Download Weight & Dimension',
        message: `จำนวนไฟล์ที่ download ในครั้งนี้คือ ${totalRound} ไฟล์`,
        confirmText: 'DOWNLOAD',
        confirmType: 'secondary',
        cancelType: '',
        cancelText: 'CANCEL',
        onConfirm: () => this.exportProductsToCSV()
      })
    },
    async exportProductsToCSV () {
      try {
        this.exporting = true
        const limit = 3000
        const totalRound = Math.ceil(this.totalProduct / limit)
        const timeStringFormat = this.$dayjs().format('DD_MM_YYYY_HHmm')
        for (let page = 1; page <= totalRound; page++) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await ProductService.getProductsInfo({
            limit,
            page,
            search: this.query.search,
            tags: this.query.tags,
            tagOperation: this.query.tagOperation,
            brand: this.query.brand,
            collection: this.query.collection
          })
          const exportInfo = data.results.flatMap(({ skus = [], ...product }) => this.sortSize(skus).map((sku) => ({
              'Image': product.photoUrls?.[0] ?? '',
              'Brand': product.brand?.toUpperCase() ?? '',
              'Collection': escapeCsv(product.gwCollection?.name)?.toUpperCase() ?? '',
              'Product Name': escapeCsv(product.name),
              'Model': product.model,
              'Code': sku.code,
              'Color': product.colors?.[0] ?? '',
              'Size': sku.size?.name ?? '',
              'Weight (gram)': sku?.weightGram ?? '',
              'Width (cm)': sku?.dimension?.widthCm ?? '',
              'Length (cm)': sku?.dimension?.lengthCm ?? '',
              'Height (cm)': sku?.dimension?.heightCm ?? ''
            }))) ?? []
          const roundInFilename = totalRound > 1 ? `_${page}` : ''
          const options = {
            filename: `product_weight_dimension_${timeStringFormat}${roundInFilename}`,
            showLabels: true,
            useKeysAsHeaders: true
          }

          const csvExporter = new ExportToCsv(options)
          csvExporter.generateCsv(exportInfo)
        }
      } catch (error) {
        console.error('fetchProductsToExport', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.code} : ${error.message}`,
          type: 'error'
        })
      } finally {
        this.exporting = false
      }
    },
    isFilled: (v) => v !== null
        && v !== undefined
        && v !== ''
        && !Number.isNaN(v),
    sortSize (skus) {
      return [...skus].sort(
        (a, b) => SizeValues(a.size.name) - SizeValues(b.size.name)
      )
    }
  }
}
</script>

<style scoped>
.info-label {
  font-size: 11px;
  color: #888;
  font-weight: 600;
}

.info-value {
  font-size: 15px;
  font-weight: 500;
}
.max-width-text-field {
  max-width: 140px;
  min-height: 88px;
}
.list-height {
  height: calc(100vh - 290px);
  overflow: scroll;
}
.text-top {
  min-height: 88px;
}
</style>
